import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { useEntityManagementEnabled } from './useEntityManagementEnabled';

/**
 * Single gating site for every write in the module (FD-4471, AC 6).
 *
 * Both halves mirror a guard that will otherwise reject the request:
 *
 * - the module flag, because `assertEnabled()` on `LegalEntityController` and
 *   `EntityRelationshipController` fail-closes on `legalStructureEnabled` with a 403
 * - `fd.internal`, because every POST / PUT / DELETE on those two controllers is
 *   `@UserAccess({ permissions: [UserPermission.INTERNAL] })`
 *
 * The reads are not gated this way — they carry only AuthGuard — so a user without write
 * access still sees the whole structure, minus the affordances to change it. Hidden
 * rather than disabled: a button that always 403s is worse than no button, and there is
 * nothing the user could do to satisfy it.
 *
 * **This is where the ticket's premise and the API disagree.** FD-4471 is written for a
 * client self-servicing their own structure, and `fd.internal` means no client user can
 * write to this module at all — so what ships is internal-only editing. That is a BE
 * decision to revisit (the same open scope question as the TODO in
 * `useEntityManagementEnabled`), not something to paper over here: matching the guard is
 * the honest behaviour, because the request would fail either way.
 */
export function useEntityManagementCanEdit(): boolean {
  const isEnabled = useEntityManagementEnabled();

  if (!isEnabled) {
    return false;
  }

  return PermissionService.get().hasPermission(PermissionKey.isInternal);
}
